<template>
  <div class="autocomplete" ref="autocompleteRef" @keydown.esc="close">
    <Input
      v-model="searchModel"
      @input="onInputChange"
      @blur="onBlur"
      @keydown.prevent.down="onArrowDown"
      @keydown.prevent.up="onArrowUp"
      @keydown.prevent.enter="onEnter"
    >
      <template #suffix>
        <Icon v-if="searchModel" class="clear-icon" name="x-close" @click="clearValue" />
      </template>
    </Input>

    <Listbox class="options-container" v-if="isOpen">
      <div class="loading" v-if="props.loading">
        <slot name="loading-indicator">
          <LoadingIndicator />
        </slot>
      </div>

      <Text variant="body-medium" align="center" class="no-result" v-if="!props.loading && !hasOptions">
        <slot name="empty-result">No results</slot>
      </Text>

      <template v-if="!props.loading && hasOptions">
        <slot />
      </template>
    </Listbox>
  </div>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core';
import { ref, onBeforeUnmount, useSlots, computed, VNode, provide, watch } from 'vue';

import Icon from '../icon.vue';
import Input from '../input.vue';
import Listbox from '../listbox/listbox.vue';
import LoadingIndicator from '../loading-indicator.vue';
import Text from '../text.vue';
import { autocompleteInjectionKey } from './autocomplete.token';

interface AutocompleteProps {
  debounce?: number;
  loading?: boolean;
  displayName: (option: any) => string;
}

const slots = useSlots();
const model = defineModel();
const emit = defineEmits<{ search: [value: string] }>();
const props = withDefaults(defineProps<AutocompleteProps>(), {
  debounce: 300,
});

const searchModel = ref<string>('');
const isOpen = ref<boolean>(false);
const activeOptionKey = ref<unknown>(null);
const selectedOptionKey = ref<unknown>(null);
const autocompleteRef = ref<HTMLElement | null>(null);

const options = computed<VNode[]>(() => (slots.default?.()[0]?.children as VNode[]) || []);
const optionsCount = computed(() => options.value.length);
const hasOptions = computed(() => !!optionsCount.value);

provide(autocompleteInjectionKey, {
  activeOptionKey,
  selectedOptionKey,
  selectOption,
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const onInputChange = useDebounceFn(() => {
  emit('search', searchModel.value);
  open();
}, props.debounce);

function handleClickOutside(event: Event) {
  if (!autocompleteRef.value?.contains(event.target as HTMLElement)) {
    close();
  }
}

function onArrowDown() {
  incrementActiveOption(1);
}

function onArrowUp() {
  incrementActiveOption(-1);
}

function incrementActiveOption(increment: 1 | -1) {
  if (isOpen.value) {
    const activeOptionIndex = options.value.findIndex(({ key }) => key === activeOptionKey.value);
    const option = options.value[activeOptionIndex + increment * 1];

    if (option) {
      activeOptionKey.value = option.key;
    }
  }
}

function onEnter() {
  if (!isOpen.value) {
    return;
  }

  if (selectedOptionKey.value === activeOptionKey.value) {
    return selectOption(model.value);
  }

  if (activeOptionKey.value !== -1) {
    selectedOptionKey.value = activeOptionKey.value;
  }
}

function onBlur() {
  setTimeout(() => {
    if (searchModel.value === '') {
      model.value = null;
    } else {
      searchModel.value = props.displayName(model.value);
    }

    close();
  }, 250);
}

function clearValue() {
  searchModel.value = '';
  selectOption(null);
}

function open() {
  isOpen.value = true;
  document.addEventListener('click', handleClickOutside);
}

function close() {
  isOpen.value = false;
  activeOptionKey.value = null;
  document.removeEventListener('click', handleClickOutside);
}

function selectOption(value: any) {
  model.value = value;
  searchModel.value = props.displayName(value);
  close();
}

watch(model, (value) => (searchModel.value = props.displayName(value)), { immediate: true });
</script>

<style scoped lang="scss">
.autocomplete {
  position: relative;
  display: inline-flex;
  flex-direction: column;
}

.clear-icon {
  cursor: pointer;
}

.loading,
.no-result {
  padding-block: var(--t-spacing-md);
}

.options-container {
  position: absolute;
  z-index: 1;
  top: 3.5rem;
  left: 0;
  right: 0;

  overflow-y: auto;
  max-height: 200px;
}
</style>
